import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { EmailRecipientItem } from '../utils/clients-hooks';

/**
 * Потвърждение преди изпращане или повторно изпращане на фактура по имейл.
 *
 * Показва всички актуални имейл адреси, към които ще бъде адресирано писмото,
 * включително обектите, от които произхожда всеки имейл.
 */
export interface SendInvoiceEmailModalProps {
    show: boolean;
    invoiceLabel: string;
    lastSentAt?: string;
    recipient?: string;
    recipients?: (string | EmailRecipientItem)[];
    isResend?: boolean;
    loading?: boolean;
    loadingRecipient?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

export const SendInvoiceEmailModal = ({
    show,
    invoiceLabel,
    lastSentAt,
    recipient,
    recipients,
    isResend = false,
    loading = false,
    loadingRecipient = false,
    onConfirm,
    onCancel,
}: SendInvoiceEmailModalProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });
    const { t: commonT } = useTranslation('translation', { keyPrefix: 'common' });

    // Извличане на нормализиран списък с имейли и свързани обекти
    const recipientItems: EmailRecipientItem[] = React.useMemo(() => {
        if (recipients && recipients.length > 0) {
            return recipients.map((item) => {
                if (typeof item === 'string') {
                    return { email: item, branches: [] };
                }
                return item;
            });
        }
        if (recipient && recipient.trim().length > 0) {
            return recipient
                .split(/[,;\s]+/)
                .map(s => s.trim())
                .filter(Boolean)
                .map(email => ({ email, branches: [] }));
        }
        return [];
    }, [recipients, recipient]);

    const hasRecipients = recipientItems.length > 0;

    return (
        <Modal show={show} onHide={onCancel} centered>
            <Modal.Header closeButton>
                <Modal.Title>{isResend ? t('resendEmailTitle') : t('sendEmailTitle')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className='mb-3 fw-bold text-primary'>{invoiceLabel}</div>
                {isResend && lastSentAt && (
                    <div className='mb-3 text-muted small'>{t('resendEmailLastSentAt')}: {lastSentAt}</div>
                )}
                {loadingRecipient ? (
                    <div className='d-flex align-items-center py-2'>
                        <Spinner animation='border' size='sm' className='me-2' />
                        <span className='text-muted'>{commonT('loading') || 'Зареждане...'}</span>
                    </div>
                ) : hasRecipients ? (
                    <div>
                        <div className='mb-2 text-secondary'>{isResend ? t('resendEmailTo') : t('sendEmailTo')}:</div>
                        <div className='d-flex flex-column gap-2'>
                            {recipientItems.map((item, idx) => (
                                <div
                                    key={idx}
                                    className='p-2 px-3 rounded border bg-light bg-opacity-50 d-flex flex-column gap-1'
                                >
                                    <div className='d-flex align-items-center gap-2'>
                                        <span className='fs-6'>✉️</span>
                                        <span className='fw-bold text-dark font-monospace' style={{ fontSize: '0.92rem' }}>
                                            {item.email}
                                        </span>
                                    </div>
                                    {item.branches && item.branches.length > 0 && (
                                        <div className='d-flex flex-wrap gap-1 align-items-center mt-1'>
                                            {item.branches.map((branchName, bIdx) => (
                                                <span
                                                    key={bIdx}
                                                    className='badge bg-white text-secondary border px-2 py-1 shadow-sm'
                                                    style={{ fontSize: '0.78rem', fontWeight: 500 }}
                                                    title={branchName}
                                                >
                                                    🏢 {branchName}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className='alert alert-danger mb-0' role='alert'>
                        ⚠️ {t('resendEmailNoRecipient')}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant='secondary' onClick={onCancel} disabled={loading}>
                    {commonT('cancel')}
                </Button>
                <Button
                    variant='primary'
                    onClick={onConfirm}
                    disabled={loading || loadingRecipient || !hasRecipients}
                >
                    {loading ? <Spinner animation='border' size='sm' /> : (isResend ? t('resendEmailConfirm') : t('sendEmailConfirm'))}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const ResendEmailModal = SendInvoiceEmailModal;
